/**
 * 73. Payslips — employee self-service; PDF; history.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatMoney } from "@/lib/money";

interface PayslipRow {
  id: string;
  payslipNumber: string;
  employee: string;
  period: string;
  payDate: string | null;
  grossPay: string;
  netPay: string;
  currency: string;
  status: "Draft" | "Posted" | "Sent";
}

export function PayslipsPage() {
  const { data, isLoading } = useApiList<PayslipRow>(
    ["payroll", "payslips"],
    "/payroll/payslips",
  );

  return (
    <div>
      <PageHeader
        title="Payslips"
        subtitle="Employee self-service payslips, PDF copies, and history."
        actions={<Button variant="primary">Download PDF</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Payslips (${data.total})`}>
          <DataTable<PayslipRow>
            rows={data.items}
            columns={[
              {
                key: "payslipNumber",
                label: "Payslip",
                render: (r) => (
                  <span className="atlas-mono">{r.payslipNumber}</span>
                ),
              },
              { key: "employee", label: "Employee" },
              { key: "period", label: "Period" },
              {
                key: "payDate",
                label: "Pay date",
                render: (r) => (r.payDate ? formatDate(r.payDate) : "—"),
              },
              {
                key: "grossPay",
                label: "Gross",
                numeric: true,
                render: (r) => formatMoney(r.grossPay, r.currency),
              },
              {
                key: "netPay",
                label: "Net",
                numeric: true,
                render: (r) => formatMoney(r.netPay, r.currency),
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Posted" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Sent" ? (
                    <Badge tone="primary">{r.status}</Badge>
                  ) : (
                    <Badge tone="default">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
